/**
 * UNRAVEL — cart bridge.
 *
 * Our custom sections (PDP, upsell strips, quick-add) post straight to the Cart AJAX API.
 * Horizon's drawer, cart badge and header actions only react to StandardEvents.cartLinesUpdate,
 * so without this bridge an add succeeds silently: no drawer, no badge change.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

const isDrawerWrite = (init) => {
  const headers = init?.headers;
  if (!headers) return false;
  if (typeof headers.get === 'function') return headers.get('X-Unravel-Source') === 'cart-drawer';
  return headers['X-Unravel-Source'] === 'cart-drawer';
};

// Shopify's own cart sync POSTs /cart/add.js during page load to merge a Shop-app
// cart. Reporting that as an 'add' would auto-open the drawer on every page view,
// so an add only counts as one when the visitor has actually interacted.
const userDriven = () => {
  const activation = navigator.userActivation;
  if (activation && typeof activation.isActive === 'boolean') return activation.isActive;
  return performance.now() > 3000;
};

const announce = (url, response) => {
  // The event carries a deferred promise that listeners await before reading the
  // cart; resolve it once we've re-read /cart.js so they never see stale totals.
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: /\/cart\/add/.test(url) && userDriven() ? 'add' : 'update',
      context: 'unravel-bridge',
      lines: [],
      promise: deferred.promise,
    })
  );

  fetch('/cart.js', { headers: { 'X-Unravel-Source': 'cart-drawer' } })
    .then((r) => r.json())
    .then((cart) => deferred.resolve?.(cart))
    .catch(() => deferred.resolve?.(null));
};

const nativeFetch = window.fetch;

window.fetch = function (input, init) {
  const url = typeof input === 'string' ? input : input?.url ?? '';
  const promise = nativeFetch.apply(this, arguments);

  if (!CART_WRITE.test(url) || isDrawerWrite(init)) return promise;

  return promise.then((response) => {
    // A throw here would reject the caller's own add-to-cart promise and flip its
    // button to an error state, so the bridge must never surface its failures.
    try {
      if (response.ok) announce(url, response);
    } catch (error) {
      console.warn('[unravel-cart-bridge]', error);
    }
    return response;
  });
};
